import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  LinearProgress,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material"
import BackIcon from "@mui/icons-material/ArrowBackOutlined"
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined"
import TimeIcon from "@mui/icons-material/ScheduleOutlined"
import BillableIcon from "@mui/icons-material/PaidOutlined"
import SignedIcon from "@mui/icons-material/VerifiedOutlined"
import PendingIcon from "@mui/icons-material/PendingActionsOutlined"
import TaskIcon from "@mui/icons-material/TaskAltOutlined"
import ProjectIcon from "@mui/icons-material/FolderSpecialOutlined"
import MailIcon from "@mui/icons-material/MailOutlineOutlined"

import dayjs from "dayjs"

import { clientApi, projectApi, taskApi, timeApi, timeReportApi, userApi } from "../api"
import { useAuth } from "../context/AuthContext"
import { usePermissions } from "../permissions"
import PageHeader from "../components/PageHeader"
import EmptyState from "../components/EmptyState"
import Figure, { deltaOf } from "../components/Figure"
import { LoadingRows } from "../components/LoadingState"
import ClientSelect from "../components/ClientSelect"
import ProjectSelect from "../components/ProjectSelect"
import SearchSelect from "../components/SearchSelect"
import QuickFilters from "../components/QuickFilters"
import DateRangePicker, { describeRange, presetRanges } from "../components/DateRangePicker"
import HoursChart from "../components/HoursChart"
import UserAvatar from "../components/UserAvatar"
import ProjectDetailDialog from "./projects/ProjectDetailDialog"
import TaskDetailDrawer from "./tasks/TaskDetailDrawer"
import { hoursLabel, humanDuration } from "../time/duration"
import { formatDate, formatDateTime, relativeTime } from "../utils/format"
import { rise } from "../motion"

/**
 * One person, and what their time went on.
 *
 * ── Why a page and not a bigger dialog ──────────────────────────────────────
 *
 * "Who is this" is a dialog: a name, a role, an account state, and you are back
 * where you were. "What has Dara been doing since June, and how much of it was
 * billable" is an afternoon's question with filters, a range and a table — it
 * needs a URL, because the answer gets sent to somebody else.
 *
 * ── Why the hours are a tab rather than a section ───────────────────────────
 *
 * The two halves have different readers. An administrator opening this from the
 * Users list wants the profile; a manager arriving from a report wants the
 * hours and nothing else. Stacked, each of them scrolls past the other's half
 * every time.
 *
 * ── Why the hours tab can be absent ─────────────────────────────────────────
 *
 * Reading somebody else's hours is `time_reports:view_all`, and the time API
 * answers a caller without it with THEIR OWN rows rather than a 403 — which on
 * a page built around a person would print the viewer's hours under that
 * person's name. So the tab is offered only where the answer can be trusted,
 * and the response's `scope` is checked as well as the permission.
 */

const DEFAULT_RANGE = presetRanges().find((preset) => preset.id === "this_month")

const VIEWS = [
  { key: "billable_only", label: "Billable only", icon: BillableIcon },
  { key: "signed_only", label: "Signed off only", icon: SignedIcon },
]

export default function UserDetailPage() {
  const { userId } = useParams()
  const navigate = useNavigate()
  const { can, isBackOffice } = usePermissions()
  const { user: viewer } = useAuth()

  const isSelf = String(viewer?.id || "") === String(userId)
  const mayReadHours = isSelf || can("time_reports", "view_all")

  const [person, setPerson] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [tab, setTab] = useState("profile")

  useEffect(() => {
    let live = true
    setLoading(true)
    userApi
      .get(userId)
      .then((data) => live && setPerson(data.user || null))
      .catch((err) => live && setError(err.userMessage || "That profile could not be loaded."))
      .finally(() => live && setLoading(false))
    return () => {
      live = false
    }
  }, [userId])

  const name = person?.name || person?.username || "Person"

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Button
        size="small"
        startIcon={<BackIcon />}
        onClick={() => navigate("/users")}
        sx={{ mb: 1, ml: -1 }}
        color="inherit"
      >
        Users
      </Button>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <PageHeader
        title={loading ? "…" : name}
        subtitle={
          person ? (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <UserAvatar name={name} email={person.email} avatarKey={person.avatar_key} size={28} tooltip={false} />
              <Typography variant="body2" color="text.secondary">
                {person.username}
              </Typography>
              {person.role_label ? <Chip size="small" label={person.role_label} sx={{ height: 20, fontSize: 11 }} /> : null}
              {isBackOffice && person.organization_label ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={person.organization_label}
                  sx={{ height: 20, fontSize: 11 }}
                />
              ) : null}
              <Chip
                size="small"
                label={person.status === "invited" ? "Invited" : person.active ? "Active" : "Deactivated"}
                color={person.status === "invited" ? "warning" : person.active ? "success" : "default"}
                sx={{ height: 20, fontSize: 11 }}
              />
            </Stack>
          ) : null
        }
        actions={
          person?.email ? (
            <Button size="small" variant="outlined" startIcon={<MailIcon />} href={`mailto:${person.email}`}>
              Email
            </Button>
          ) : null
        }
      />

      <Tabs value={tab} onChange={(_event, value) => setTab(value)} sx={{ mb: 2 }}>
        <Tab value="profile" label="Profile" />
        {mayReadHours ? <Tab value="hours" label="Hours logged" /> : null}
      </Tabs>

      {loading ? (
        <Card>
          <CardContent>
            <LoadingRows rows={5} />
          </CardContent>
        </Card>
      ) : !person ? (
        <EmptyState icon={TaskIcon} title="No such person" description="This account does not exist, or is not yours to see." />
      ) : tab === "profile" ? (
        <ProfileTab person={person} isSelf={isSelf} />
      ) : (
        <HoursTab person={person} isSelf={isSelf} />
      )}
    </Box>
  )
}

/* ──────────────────────────────────────────────────────────────────────────
   Profile
   ────────────────────────────────────────────────────────────────────────── */

/**
 * Who they are, and how much of the platform's work has their name on it.
 *
 * The counts are asked for one at a time and each is allowed to fail on its
 * own: a viewer who can read people but not projects gets the profile with one
 * card missing, rather than a page that refuses to load because of a module
 * they were never granted.
 */
function ProfileTab({ person, isSelf }) {
  const { can } = usePermissions()
  const [counts, setCounts] = useState({ tasks: null, projects: null })

  useEffect(() => {
    let live = true
    if (can("tasks", "view")) {
      taskApi
        .list({ assignee_id: person.id, open: "true", limit: 1 })
        .then((data) => live && setCounts((current) => ({ ...current, tasks: Number(data.total || 0) })))
        .catch(() => live && setCounts((current) => ({ ...current, tasks: null })))
    }
    if (can("projects", "view")) {
      projectApi
        .list({ owner_id: person.id, limit: 1 })
        .then((data) => live && setCounts((current) => ({ ...current, projects: Number(data.total || 0) })))
        .catch(() => live && setCounts((current) => ({ ...current, projects: null })))
    }
    return () => {
      live = false
    }
  }, [person.id, can])

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <Figure
          icon={TaskIcon}
          label="Open tasks"
          value={counts.tasks === null ? "—" : String(counts.tasks)}
          caption={counts.tasks === null ? "not visible to you" : "assigned and not done"}
        />
        <Figure
          icon={ProjectIcon}
          label="Projects owned"
          value={counts.projects === null ? "—" : String(counts.projects)}
          caption={counts.projects === null ? "not visible to you" : "they are the owner of record"}
        />
        <Figure
          icon={TimeIcon}
          label="Last seen"
          value={person.last_access_on ? relativeTime(person.last_access_on) : "Never"}
          caption={person.last_access_on ? formatDateTime(person.last_access_on) : "this account has not signed in"}
        />
      </Stack>

      <Card sx={{ ...rise(1) }}>
        <CardContent>
          <Typography variant="h3" component="h2" gutterBottom>
            Details
          </Typography>
          <Stack spacing={1.25} divider={<Divider flexItem />}>
            <Row label="Full name" value={person.name} />
            <Row label="Signs in as" value={person.username} />
            <Row label="Email" value={person.email} />
            <Row label="Job title" value={person.title} />
            <Row label="Phone" value={person.phone} />
            <Row label="Department" value={person.department_label} />
            <Row label="Role" value={person.role_label || person.role_name} />
            <Row label="Organization" value={person.organization_label || person.organization_id} />
            <Row label="Account created" value={person.created_at ? formatDate(person.created_at) : null} />
            <Row
              label="Data scope"
              value={person.data_scope ? String(person.data_scope).replace(/_/g, " ") : null}
            />
          </Stack>
          {isSelf ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              This is your own account. Your photo, password and preferences are in Settings.
            </Alert>
          ) : null}
        </CardContent>
      </Card>
    </Stack>
  )
}

function Row({ label, value }) {
  return (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0, sm: 2 }} alignItems={{ sm: "baseline" }}>
      <Typography variant="caption" color="text.secondary" sx={{ width: 150, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ minWidth: 0, wordBreak: "break-word" }}>
        {value || "—"}
      </Typography>
    </Stack>
  )
}

/* ──────────────────────────────────────────────────────────────────────────
   Hours logged
   ────────────────────────────────────────────────────────────────────────── */

function HoursTab({ person, isSelf }) {
  const { can, isBackOffice } = usePermissions()

  const [range, setRange] = useState({ from: DEFAULT_RANGE.from, to: DEFAULT_RANGE.to })
  const [filters, setFilters] = useState({ client_id: "", project_id: "", task_id: "", billable_only: false, signed_only: false })
  const [splitBy, setSplitBy] = useState("client")

  const [report, setReport] = useState(null)
  const [split, setSplit] = useState(null)
  const [entries, setEntries] = useState(null)
  const [entryTotal, setEntryTotal] = useState(0)
  const [entryScope, setEntryScope] = useState(null)
  const [loading, setLoading] = useState(true)
  const [more, setMore] = useState(false)
  const [error, setError] = useState(null)
  const [exporting, setExporting] = useState(false)

  const [clients, setClients] = useState([])
  const [projects, setProjects] = useState([])
  const [tasks, setTasks] = useState([])

  const [openProjectId, setOpenProjectId] = useState(null)
  const [openTaskId, setOpenTaskId] = useState(null)
  const [statuses, setStatuses] = useState([])

  const PAGE = 50

  /* The query every panel on this tab is built from, in one place: the report,
     the split and the list have to describe the same hours or the page argues
     with itself. */
  const query = useMemo(
    () => ({
      user_id: person.id,
      from: range.from || undefined,
      to: range.to || undefined,
      client_id: filters.client_id || undefined,
      project_id: filters.project_id || undefined,
      ...(filters.billable_only ? { billable: "true" } : {}),
      ...(filters.signed_only ? { signed: "true" } : {}),
    }),
    [person.id, range.from, range.to, filters.client_id, filters.project_id, filters.billable_only, filters.signed_only],
  )

  useEffect(() => {
    if (isBackOffice && can("clients", "view")) {
      clientApi
        .list()
        .then((data) => setClients(data.organizations || []))
        .catch(() => setClients([]))
    }
    if (can("projects", "view")) {
      projectApi
        .list({ limit: 200 })
        .then((data) => setProjects(data.projects || []))
        .catch(() => setProjects([]))
    }
    if (can("tasks", "view")) {
      taskApi
        .statuses()
        .then((data) => setStatuses(data.statuses || []))
        .catch(() => setStatuses([]))
    }
  }, [can, isBackOffice])

  const loadTasks = useCallback(
    (search) => {
      if (!can("tasks", "view")) return
      const params = { limit: 50 }
      if (filters.client_id) params.client_id = filters.client_id
      if (filters.project_id) params.project_id = filters.project_id
      if (search) params.q = search
      taskApi
        .list(params)
        .then((data) => setTasks(data.tasks || []))
        .catch(() => setTasks([]))
    },
    [can, filters.client_id, filters.project_id],
  )

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [summary, list] = await Promise.all([
        timeReportApi.summary({ ...query, group_by: "day" }),
        timeApi.entries({ ...query, task_id: filters.task_id || undefined, limit: PAGE, offset: 0 }),
      ])
      setReport(summary)
      setEntries(list.entries || [])
      setEntryTotal(Number(list.total || 0))
      setEntryScope(list.scope || null)
    } catch (err) {
      setError(err.userMessage || "Those hours could not be loaded.")
      setReport(null)
      setEntries([])
    } finally {
      setLoading(false)
    }
  }, [query, filters.task_id])

  useEffect(() => {
    load()
  }, [load])

  /* The split is its own request because only one of the two is on screen.
     Fetching both to keep a toggle instant is two thirds of the work thrown
     away on every filter change. */
  useEffect(() => {
    let live = true
    setSplit(null)
    timeReportApi
      .summary({ ...query, group_by: splitBy })
      .then((data) => live && setSplit(data))
      .catch(() => live && setSplit({ groups: [] }))
    return () => {
      live = false
    }
  }, [query, splitBy])

  const loadMore = async () => {
    setMore(true)
    try {
      const next = await timeApi.entries({
        ...query,
        task_id: filters.task_id || undefined,
        limit: PAGE,
        offset: entries.length,
      })
      setEntries((current) => [...current, ...(next.entries || [])])
    } catch (err) {
      setError(err.userMessage || "No more could be loaded.")
    } finally {
      setMore(false)
    }
  }

  const exportCsv = async () => {
    setExporting(true)
    try {
      await timeReportApi.exportCsv({ ...query, group_by: splitBy })
    } catch (err) {
      setError(err.userMessage || "That export could not be built.")
    } finally {
      setExporting(false)
    }
  }

  const totals = report?.totals
  const before = report?.previous?.totals
  const busiest = (split?.groups || []).reduce((most, row) => Math.max(most, row.tracked_seconds), 0)

  /* Asking for one person and being handed your own hours is the one failure
     this page must never render silently. The permission is checked before the
     tab is offered; this catches a grant that changed under a loaded page. */
  const wrongPerson = entryScope === "own" && !isSelf

  return (
    <Stack spacing={2}>
      <Card sx={{ ...rise() }}>
        <CardContent>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="flex-start">
            <DateRangePicker
              from={range.from}
              to={range.to}
              onChange={(next) => setRange({ from: next.from, to: next.to })}
            />
            {isBackOffice && clients.length > 0 ? (
              <ClientSelect
                clients={clients}
                value={filters.client_id}
                onChange={(value) => setFilters((current) => ({ ...current, client_id: value, project_id: "", task_id: "" }))}
                emptyLabel="All clients"
                sx={{ minWidth: 200 }}
              />
            ) : null}
            <ProjectSelect
              projects={
                filters.client_id
                  ? projects.filter((project) => String(project.client_id) === String(filters.client_id))
                  : projects
              }
              value={filters.project_id}
              onChange={(value) => setFilters((current) => ({ ...current, project_id: value, task_id: "" }))}
              emptyLabel="All projects"
              sx={{ minWidth: 200 }}
            />
            <SearchSelect
              options={tasks}
              value={filters.task_id}
              onChange={(value) => setFilters((current) => ({ ...current, task_id: value }))}
              label="Task"
              emptyLabel="All tasks"
              getLabel={(task) => (task?.task_code ? `${task.task_code}: ${task.title}` : task?.title || "")}
              getCaption={(task) => task?.project_name || task?.client_label || null}
              getSearchText={(task) => `${task.task_code ?? ""} ${task.project_name ?? ""}`}
              onSearch={loadTasks}
              searchKey={`${filters.client_id}:${filters.project_id}`}
              noOptionsText="No task matches"
              sx={{ minWidth: 220 }}
            />
            <Box sx={{ flex: 1 }} />
            {can("time_reports", "export") ? (
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={exportCsv}
                disabled={exporting || loading}
              >
                {exporting ? "Preparing…" : "Export"}
              </Button>
            ) : null}
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
            <QuickFilters
              items={VIEWS}
              values={filters}
              onToggle={(key) => setFilters((current) => ({ ...current, [key]: !current[key] }))}
            />
            <Box sx={{ flex: 1 }} />
            {/* The dates themselves, not the preset's name: the button beside
                these filters already says "This Month", and a caption repeating
                it is a second copy of the same word rather than the thing it
                leaves unsaid. */}
            <Typography variant="caption" color="text.secondary">
              {range.from && range.to
                ? `${dayjs(range.from).format("MMM D")} – ${dayjs(range.to).format("MMM D, YYYY")}`
                : describeRange(range.from, range.to)}
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {wrongPerson ? (
        <Alert severity="warning">
          These are your own hours, not theirs — reading somebody else's needs the "all time" permission.
        </Alert>
      ) : null}

      {loading ? (
        <Card>
          <CardContent>
            <LoadingRows rows={4} />
          </CardContent>
        </Card>
      ) : (
        <>
          {totals ? (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Figure
                icon={TimeIcon}
                label="Tracked"
                value={hoursLabel(totals.tracked_seconds)}
                caption={`${totals.entry_count} ${totals.entry_count === 1 ? "entry" : "entries"}`}
                delta={deltaOf(totals.tracked_seconds, before?.tracked_seconds, hoursLabel)}
              />
              <Figure
                icon={BillableIcon}
                label="Billable"
                value={hoursLabel(totals.billable_seconds)}
                caption={
                  totals.tracked_seconds > 0
                    ? `${Math.round((totals.billable_seconds / totals.tracked_seconds) * 100)}% of tracked`
                    : "nothing tracked"
                }
                delta={deltaOf(totals.billable_seconds, before?.billable_seconds, hoursLabel)}
              />
              <Figure
                icon={SignedIcon}
                label="Signed off"
                value={hoursLabel(totals.signed_seconds)}
                caption={
                  totals.tracked_seconds > 0
                    ? `${Math.round((totals.signed_seconds / totals.tracked_seconds) * 100)}% of tracked`
                    : "nothing to sign"
                }
              />
              <Figure
                icon={PendingIcon}
                label="Awaiting sign-off"
                value={hoursLabel(Math.max(0, totals.tracked_seconds - totals.signed_seconds))}
                caption="cannot reach an invoice yet"
              />
            </Stack>
          ) : null}

          {/* The shape over the range. One bar a day says more about a week
              than any total does — a flat 40 and four ten-hour days are the
              same number and not the same month. */}
          {report?.groups?.length > 1 ? (
            <Card sx={{ ...rise(1) }}>
              <CardContent>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Tracked by day
                </Typography>
                <HoursChart
                  points={[...report.groups]
                    .sort((a, b) => String(a.group_key).localeCompare(String(b.group_key)))
                    .map((row) => ({
                      key: row.group_key,
                      value: row.tracked_seconds,
                      label: hoursLabel(row.tracked_seconds),
                      hint: dayjs(row.group_key).format("MMM D"),
                    }))}
                />
              </CardContent>
            </Card>
          ) : null}

          <Card sx={{ ...rise(2) }}>
            <CardContent sx={{ pb: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
                <Typography variant="h3" component="h2" sx={{ flex: 1 }}>
                  Where the time went
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={splitBy}
                  onChange={(_event, value) => value && setSplitBy(value)}
                >
                  <ToggleButton value="client">By client</ToggleButton>
                  <ToggleButton value="project">By project</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </CardContent>
            {split === null ? (
              <CardContent>
                <LoadingRows rows={3} height={28} />
              </CardContent>
            ) : split.groups.length === 0 ? (
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  Nothing logged in this range.
                </Typography>
              </CardContent>
            ) : (
              <Box sx={{ px: 2, pb: 2 }}>
                <Stack spacing={1}>
                  {split.groups.map((row) => (
                    <Stack key={String(row.group_key)} direction="row" alignItems="center" spacing={1.5}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {/* A project row opens the project, the same panel the
                            rest of the app opens. A client is a name here and
                            nothing to open. */}
                        {splitBy === "project" && row.group_key ? (
                          <LinkText onClick={() => setOpenProjectId(Number(row.group_key))}>{row.group_label}</LinkText>
                        ) : (
                          <Typography variant="body2" noWrap>
                            {row.group_label}
                          </Typography>
                        )}
                      </Box>
                      <Tooltip title={`${Math.round((row.tracked_seconds / (busiest || 1)) * 100)}% of the busiest`}>
                        <LinearProgress
                          variant="determinate"
                          value={busiest ? (row.tracked_seconds / busiest) * 100 : 0}
                          sx={{ width: { xs: 90, sm: 180 }, height: 6, borderRadius: 3 }}
                        />
                      </Tooltip>
                      <Typography
                        variant="body2"
                        sx={{ width: 86, textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 600 }}
                      >
                        {hoursLabel(row.tracked_seconds)}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            )}
          </Card>

          <Card sx={{ ...rise(3) }}>
            <CardContent sx={{ pb: 0 }}>
              <Stack direction="row" alignItems="baseline" spacing={1}>
                <Typography variant="h3" component="h2" sx={{ flex: 1 }}>
                  Entries
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {entries && entryTotal > entries.length
                    ? `showing ${entries.length} of ${entryTotal}`
                    : `${entryTotal} in this range`}
                </Typography>
              </Stack>
            </CardContent>

            {!entries || entries.length === 0 ? (
              <EmptyState
                icon={TimeIcon}
                title="Nothing logged in this range"
                description="Widen the dates, or clear a filter — the range and the filters above decide this list."
              />
            ) : (
              <>
                <Table size="small" sx={{ mt: 1 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 120 }}>Day</TableCell>
                      <TableCell>What</TableCell>
                      <TableCell>Where</TableCell>
                      <TableCell align="right" sx={{ width: 90 }}>
                        Tracked
                      </TableCell>
                      <TableCell sx={{ width: 140 }}>State</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {entries.map((entry) => (
                      <TableRow key={entry.id} hover>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Typography variant="body2">{formatDate(entry.started_at)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {dayjs(entry.started_at).format("HH:mm")}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.75} alignItems="baseline">
                            {entry.task_code ? (
                              <LinkText onClick={() => setOpenTaskId(entry.task_id)} bold>
                                {entry.task_code}
                              </LinkText>
                            ) : null}
                            <Typography variant="body2" sx={{ minWidth: 0 }}>
                              {entry.description || entry.task_title || "Untitled"}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {entry.client_label || "—"}
                          </Typography>
                          {entry.project_name ? (
                            <LinkText onClick={() => setOpenProjectId(entry.project_id)} small>
                              {entry.project_name}
                            </LinkText>
                          ) : null}
                        </TableCell>
                        {/* `humanDuration` and not the report's own formatter:
                            a six-second entry is "6s" here and "—" there, and
                            on a list of rows an em dash reads as a missing
                            value rather than a short one. */}
                        <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
                          {humanDuration(entry.duration_seconds)}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5}>
                            {!entry.is_billable ? (
                              <Chip size="small" label="Not billable" variant="outlined" sx={{ height: 20, fontSize: 10 }} />
                            ) : null}
                            {entry.signed_off_at ? (
                              <Chip
                                size="small"
                                color="success"
                                icon={<SignedIcon sx={{ fontSize: 13 }} />}
                                label="Signed"
                                sx={{ height: 20, fontSize: 10 }}
                              />
                            ) : (
                              <Chip size="small" label="Unsigned" variant="outlined" sx={{ height: 20, fontSize: 10 }} />
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {entryTotal > entries.length ? (
                  <Box sx={{ p: 2, textAlign: "center" }}>
                    <Button onClick={loadMore} disabled={more}>
                      {more ? "Loading…" : `Show ${Math.min(PAGE, entryTotal - entries.length)} more`}
                    </Button>
                  </Box>
                ) : null}
              </>
            )}
          </Card>
        </>
      )}

      <ProjectDetailDialog projectId={openProjectId} onClose={() => setOpenProjectId(null)} />
      <TaskDetailDrawer taskId={openTaskId} statuses={statuses} onClose={() => setOpenTaskId(null)} />
    </Stack>
  )
}

/**
 * A word that opens something.
 *
 * A real button rather than a span with a click handler, so it is reachable by
 * keyboard and announced as something that can be pressed — the two things a
 * div loses silently.
 */
function LinkText({ children, onClick, bold = false, small = false }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        appearance: "none",
        border: 0,
        background: "none",
        p: 0,
        font: "inherit",
        textAlign: "left",
        cursor: "pointer",
        color: "primary.dark",
        fontWeight: bold ? 600 : 500,
        fontSize: small ? 12 : "inherit",
        maxWidth: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        display: "block",
        "&:hover": { textDecoration: "underline" },
        "&:focus-visible": { outline: (theme) => `2px solid ${theme.palette.primary.dark}`, outlineOffset: 2 },
      }}
    >
      {children}
    </Box>
  )
}
